import { useEffect, useMemo, useRef, useState } from "react";
import * as atlas from "azure-maps-control";

type MapBounds = {
    topLeft: { longitude: number; latitude: number };
    topRight: { longitude: number; latitude: number };
    botLeft: { longitude: number; latitude: number };
    botRight: { longitude: number; latitude: number };
};

type AzureConfigMapComponentProps = {
    map?: any;
    allowWorldView?: boolean;
    fitToMapBounds?: boolean;
    onMapBoundsChange?: (bounds: MapBounds) => void;
};

const isFiniteNumber = (value: any) => {
    if (value === null || value === undefined || value === "") {
        return false;
    }
    return Number.isFinite(Number(value));
};
const MILAN_CENTER: [number, number] = [9.19, 45.4642];

const buildBoundsArray = (map: any) => [
    map?.botLeft?.longitude,
    map?.botLeft?.latitude,
    map?.topRight?.longitude,
    map?.topRight?.latitude,
];

const getDefaultCenter = (map: any): [number, number] => {
    const botLeftLatitude = map?.botLeft?.latitude;
    const topLeftLatitude = map?.topLeft?.latitude;
    const botLeftLongitude = map?.botLeft?.longitude;
    const botRightLongitude = map?.botRight?.longitude;

    if (
        !isFiniteNumber(botLeftLatitude) ||
        !isFiniteNumber(topLeftLatitude) ||
        !isFiniteNumber(botLeftLongitude) ||
        !isFiniteNumber(botRightLongitude)
    ) {
        return MILAN_CENTER;
    }

    const latitude = (Number(botLeftLatitude) + Number(topLeftLatitude)) / 2;
    const longitude = (Number(botLeftLongitude) + Number(botRightLongitude)) / 2;

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return MILAN_CENTER;
    }

    return [longitude, latitude];
};

export const AzureConfigMapComponent = ({
    map,
    allowWorldView,
    fitToMapBounds,
    onMapBoundsChange,
}: AzureConfigMapComponentProps) => {
    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<atlas.Map | null>(null);
    const [mapReady, setMapReady] = useState(false);

    const lastBoundsRef = useRef<[number, number, number, number] | null>(null);
    const lastFitBoundsRef = useRef<[number, number, number, number] | null>(null);

    const worldViewEnabled = Boolean(allowWorldView);

    const boundingBox = useMemo(() => buildBoundsArray(map), [map]);
    const validBoundingBox =
        Array.isArray(boundingBox) &&
        boundingBox.length === 4 &&
        boundingBox.every((value) => isFiniteNumber(value));

    const minZoomLevel = worldViewEnabled ? 1 : 5;
    const maxBoundsOption = worldViewEnabled ? undefined : (validBoundingBox ? boundingBox : undefined);

    useEffect(() => {
        if (!mapContainerRef.current || mapRef.current) {
            return;
        }

        const [defaultLongitude, defaultLatitude] = getDefaultCenter(map);

        const mapInstance = new atlas.Map(mapContainerRef.current, {
            style: "satellite_road_labels",
            center: [defaultLongitude, defaultLatitude],
            zoom: 15.75,
            minZoom: minZoomLevel,
            maxZoom: 20,
            maxBounds: maxBoundsOption,
            language: "it-IT",
            authOptions: {
                authType: atlas.AuthenticationType.subscriptionKey,
                subscriptionKey: process.env.REACT_APP_AZURE_MAPS_KEY,
            },
            attributionControl: false,
        });

        mapRef.current = mapInstance;

        mapInstance.events.add("ready", () => {
            mapInstance.controls.add(
                [new atlas.control.ZoomControl(), new atlas.control.FullscreenControl()],
                { position: atlas.ControlPosition.TopRight }
            );

            mapInstance.controls.add(
                new atlas.control.StyleControl({
                    mapStyles: ["satellite_road_labels", "satellite", "road"],
                    layout: "icons",
                }),
                { position: atlas.ControlPosition.TopRight }
            );

            mapInstance.controls.add(
                new atlas.control.ScaleControl({ unit: "metric" }),
                { position: atlas.ControlPosition.TopRight }
            );

            setMapReady(true);
        });

        return () => {
            mapInstance.dispose();
            mapRef.current = null;
        };
    }, []);

    useEffect(() => {
        const mapInstance = mapRef.current;
        if (!mapInstance || !mapReady) {
            return;
        }

        mapInstance.setCamera({
            minZoom: minZoomLevel,
            maxBounds: maxBoundsOption,
        });
    }, [mapReady, minZoomLevel, maxBoundsOption]);

    useEffect(() => {
        if (!fitToMapBounds || !mapReady) {
            return;
        }

        const mapInstance = mapRef.current;
        if (!mapInstance) {
            return;
        }

        const boundsCandidate = buildBoundsArray(map);
        const boundsValid =
            Array.isArray(boundsCandidate) &&
            boundsCandidate.length === 4 &&
            boundsCandidate.every((value) => isFiniteNumber(value));

        if (!boundsValid) {
            const [defaultLongitude, defaultLatitude] = getDefaultCenter(map);
            mapInstance.setCamera({ center: [defaultLongitude, defaultLatitude], zoom: 15.75 });
            return;
        }

        const numericBounds: [number, number, number, number] = [
            Number(boundsCandidate[0]),
            Number(boundsCandidate[1]),
            Number(boundsCandidate[2]),
            Number(boundsCandidate[3]),
        ];

        const previous = lastFitBoundsRef.current;
        const boundsChanged = !previous || previous.some((value, index) => Math.abs(value - numericBounds[index]) > 1e-6);
        if (!boundsChanged) {
            return;
        }

        lastFitBoundsRef.current = numericBounds;
        mapInstance.setCamera({ bounds: numericBounds, padding: 40 });
    }, [fitToMapBounds, mapReady, map]);

    useEffect(() => {
        const mapInstance = mapRef.current;
        if (!mapInstance) {
            return;
        }

        const extractBounds = (boundsValue: any) => {
            if (Array.isArray(boundsValue) && boundsValue.length >= 4) {
                return boundsValue;
            }

            if (boundsValue && typeof boundsValue === "object") {
                const west = boundsValue.getWest?.() ?? boundsValue.west ?? boundsValue.minLongitude ?? boundsValue.minLon;
                const south = boundsValue.getSouth?.() ?? boundsValue.south ?? boundsValue.minLatitude ?? boundsValue.minLat;
                const east = boundsValue.getEast?.() ?? boundsValue.east ?? boundsValue.maxLongitude ?? boundsValue.maxLon;
                const north = boundsValue.getNorth?.() ?? boundsValue.north ?? boundsValue.maxLatitude ?? boundsValue.maxLat;

                if ([west, south, east, north].every((value) => isFiniteNumber(value))) {
                    return [west, south, east, north];
                }
            }

            return null;
        };

        const handleMove = () => {
            if (typeof onMapBoundsChange !== "function") {
                return;
            }

            const mapWithBounds = mapInstance as unknown as { getBounds?: () => any };
            const boundsValue = typeof mapWithBounds.getBounds === "function"
                ? mapWithBounds.getBounds()
                : mapInstance.getCamera()?.bounds;
            const bounds = extractBounds(boundsValue);
            if (!bounds) {
                return;
            }

            const numericBounds: [number, number, number, number] = [
                Number(bounds[0]),
                Number(bounds[1]),
                Number(bounds[2]),
                Number(bounds[3]),
            ];

            const previous = lastBoundsRef.current;
            const boundsChanged = !previous || previous.some((value, index) => Math.abs(value - numericBounds[index]) > 1e-6);
            if (!boundsChanged) {
                return;
            }

            lastBoundsRef.current = numericBounds;

            const [west, south, east, north] = numericBounds;
            onMapBoundsChange({
                topLeft: { longitude: west, latitude: north },
                topRight: { longitude: east, latitude: north },
                botLeft: { longitude: west, latitude: south },
                botRight: { longitude: east, latitude: south },
            });
        };

        mapInstance.events.add("moveend", handleMove);
        return () => mapInstance.events.remove("moveend", handleMove);
    }, [onMapBoundsChange]);

    return <div ref={mapContainerRef} style={{ height: "100%", width: "100%" }} />;
};
